/**
 * Translate entity status / priority / type slugs through the canonical
 * `settings.<namespace>.<value>` translation keys (the same ones the entity
 * list pages use), falling back to a humanized slug when a key is missing.
 *
 * Use this everywhere a raw backend slug (e.g. `in_progress`, `on-hold`) would
 * otherwise be shown to the user, so analytics/dashboards stay localized.
 */

export type TranslateFn = (key: string, fallback?: string) => string;

/** Humanize a slug: `in_progress` / `in-progress` -> `In progress`. */
export function humanizeSlug(value: string): string {
    const normalized = String(value ?? '')
        .replaceAll('_', ' ')
        .replaceAll('-', ' ')
        .trim()
        .toLowerCase();
    if (!normalized) {
        return normalized;
    }
    return normalized[0].toUpperCase() + normalized.slice(1);
}

/**
 * Look up `settings.<namespace>.<key>`, returning null when the key is missing.
 *
 * `t()` echoes the key back when it cannot resolve it, which is how we tell a
 * real translation apart from a miss.
 */
function lookup(t: TranslateFn, namespace: string, key: string): string | null {
    if (!key) {
        return null;
    }
    const fullKey = `settings.${namespace}.${key}`;
    const translated = t(fullKey, fullKey);
    return translated === fullKey ? null : translated;
}

/**
 * Last-resort label when no translation key matches.
 *
 * Settings values come in two shapes: lowercase slugs the backend generates
 * (`in_progress`) and human-authored display strings admins type into Settings
 * (`Client`, `Retail & E-commerce`). Humanizing the latter would lowercase
 * words the admin deliberately capitalized, so only slugs get humanized.
 */
function fallbackLabel(raw: string): string {
    return /[A-Z]/.test(raw) ? raw : humanizeSlug(raw);
}

/**
 * Resolve a settings value to its translated label.
 *
 * Values reach us in two shapes, so the lookup cascades:
 *   1. the value exactly as stored — admin-authored display strings such as
 *      `Non-Profit & NGOs` are their own key, and must be tried before any
 *      normalization mangles their punctuation;
 *   2. the `-`→`_` slug form, for backend slugs like `on-hold`;
 *   3. that slug lowercased, so capitalized stored values (`Client`) still
 *      reach their lowercase lang key (`client`);
 *   4. failing all of that, a readable rendering of the value itself.
 *
 * @param namespace one of the `settings.*` groups, e.g. 'work_order_statuses',
 *   'work_order_priorities', 'project_statuses', 'lead_statuses',
 *   'company_priorities', 'company_types', 'project_task_statuses'.
 */
export function settingLabel(t: TranslateFn, namespace: string, value: string | null | undefined): string {
    const raw = String(value ?? '');
    if (!raw) {
        return '';
    }

    return trySettingLabel(t, namespace, raw) ?? fallbackLabel(raw);
}

/**
 * Same cascade as `settingLabel`, but returns null instead of a fallback when
 * no translation key matches.
 *
 * Use this where a caller has its own, better fallback than the raw value —
 * e.g. a badge that would otherwise show an English label stored in the
 * settings JSON and needs to know whether a real translation exists first.
 */
export function trySettingLabel(t: TranslateFn, namespace: string, value: string | null | undefined): string | null {
    const raw = String(value ?? '');
    if (!raw) {
        return null;
    }
    const slug = raw.replace(/-/g, '_');

    return lookup(t, namespace, raw) ?? lookup(t, namespace, slug) ?? lookup(t, namespace, slug.toLowerCase());
}
